import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FileNode } from '../protocol';
import { nodeAtPath, trailAtPath } from '../fileTreeNav';

export interface SyncDestPickerProps {
  root: FileNode;
  listHostDir: (relPath: string) => Promise<FileNode>;
  /** Share-root-relative path the user confirmed, or null if not chosen yet. */
  selectedRel: string | null;
  onSelectRel: (rel: string) => void;
  initialRel?: string;
}

export function SyncDestPicker({
  root,
  listHostDir,
  selectedRel,
  onSelectRel,
  initialRel = '',
}: SyncDestPickerProps): JSX.Element {
  const [relPath, setRelPath] = useState(initialRel.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/$/, ''));
  const [current, setCurrent] = useState<FileNode>(() => nodeAtPath(root, relPath));

  const refreshAt = useCallback(
    async (rel: string) => {
      const norm = rel.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/$/, '');
      try {
        const listed = await listHostDir(norm);
        setCurrent(listed);
        setRelPath(norm);
      } catch {
        setCurrent(nodeAtPath(root, norm));
        setRelPath(norm);
      }
    },
    [listHostDir, root]
  );

  useEffect(() => {
    void refreshAt(relPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial open only
  }, []);

  const path = useMemo(() => trailAtPath(root, relPath), [root, relPath]);
  const dirs = useMemo(() => {
    const kids = current.children ?? [];
    return [...kids]
      .filter((c) => c.kind === 'dir')
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [current]);

  const enter = (node: FileNode) => {
    void refreshAt(node.path);
  };

  const goUp = () => {
    if (!relPath) return;
    const parts = relPath.split('/');
    parts.pop();
    void refreshAt(parts.join('/'));
  };

  const displayPath = relPath || '(share root)';

  return (
    <div className="sync-dest-picker">
      <p className="sync-dest-hint">
        Choose where on the host this sync job will live. Open folders from the shared root, then
        confirm the destination.
      </p>
      <nav className="sync-dest-crumb" aria-label="Destination path">
        {path.map((node, i) => (
          <span key={node.path || 'root'}>
            {i > 0 && ' / '}
            <button type="button" className="linkish" onClick={() => void refreshAt(node.path)}>
              {node.name || 'Shared root'}
            </button>
          </span>
        ))}
      </nav>
      <div className="sync-dest-toolbar">
        <button type="button" className="btn ghost" disabled={!relPath} onClick={goUp}>
          ↑ Up
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => onSelectRel(relPath.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/$/, ''))}
        >
          Use this folder as destination
        </button>
      </div>
      {selectedRel !== null && (
        <p className="sync-dest-selected">
          Destination: <strong>{selectedRel || '(share root)'}</strong>
        </p>
      )}
      <ul className="sync-dest-list">
        {dirs.length === 0 ? (
          <li className="muted">No subfolders here — use the button above for {displayPath}.</li>
        ) : (
          dirs.map((d) => (
            <li key={d.path}>
              <button type="button" className="sync-dest-folder" onClick={() => enter(d)}>
                📁 {d.name}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
